import { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { Loader2, Save, Plus, Trash2, Info } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";

// prices dict <-> "CODE:amount, CODE:amount" string
const toStr = (m) => Object.entries(m || {}).map(([k, v]) => `${k}:${v}`).join(", ");
const toMap = (s) => {
  const out = {};
  (s || "").split(",").forEach((pair) => {
    const [k, v] = pair.split(":").map((x) => (x || "").trim());
    if (k && v !== undefined && v !== "" && !isNaN(Number(v))) out[k.toUpperCase()] = Number(v);
  });
  return out;
};

export default function AdminAdPricing() {
  const { data } = useQuery({ queryKey: ["admin-settings"], queryFn: async () => (await api.get("/admin/settings")).data });
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (data) setS(data); }, [data]);
  if (!s) return <p className="text-muted-foreground">Loading...</p>;
  const c = s.commerce || {};
  const setC = (patch) => setS((p) => ({ ...p, commerce: { ...(p.commerce || {}), ...patch } }));

  const save = async () => {
    setBusy(true);
    try { await api.put("/admin/settings", s); toast.success("Pricing saved"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const updPkg = (i, patch) => { const arr = [...(c.packages || [])]; arr[i] = { ...arr[i], ...patch }; setC({ packages: arr }); };
  const updAddon = (i, patch) => { const arr = [...(c.addons || [])]; arr[i] = { ...arr[i], ...patch }; setC({ addons: arr }); };

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div><h1 className="font-display text-2xl font-bold">Ad Pricing & Currency</h1><p className="text-sm text-muted-foreground">Edit packages, add-ons, currencies and tax — no code required.</p></div>
        <Button className="rounded-full" onClick={save} disabled={busy} data-testid="pricing-save">{busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />} Save</Button>
      </div>

      <div className="mt-4 flex items-start gap-2 rounded-xl border border-border bg-card/40 p-3 text-xs text-muted-foreground">
        <Info className="mt-0.5 h-4 w-4 shrink-0 text-primary" /> Prices are entered as <code>CODE:amount</code> pairs, e.g. <code>INR:9999, USD:139</code>. Leave empty for a custom-quote package.
      </div>

      <Tabs defaultValue="packages" className="mt-5">
        <TabsList><TabsTrigger value="packages">Packages</TabsTrigger><TabsTrigger value="addons">Add-ons</TabsTrigger><TabsTrigger value="currency">Currency & Tax</TabsTrigger></TabsList>

        <TabsContent value="packages" className="mt-4 space-y-4">
          {(c.packages || []).map((p, i) => (
            <div key={p.id} className="bs-card p-5" data-testid={`pkg-edit-${p.id}`}>
              <div className="grid gap-3 sm:grid-cols-2">
                <div><Label className="text-xs">Name</Label><Input value={p.name} onChange={(e) => updPkg(i, { name: e.target.value })} className="mt-1" /></div>
                <div><Label className="text-xs">Tagline</Label><Input value={p.tagline} onChange={(e) => updPkg(i, { tagline: e.target.value })} className="mt-1" /></div>
                <div><Label className="text-xs">Period</Label><Input value={p.period} onChange={(e) => updPkg(i, { period: e.target.value })} className="mt-1" /></div>
                <div className="flex items-end gap-2"><Label className="text-xs">Most popular</Label><Switch checked={!!p.popular} onCheckedChange={(v) => updPkg(i, { popular: v })} /></div>
                <div className="sm:col-span-2"><Label className="text-xs">Prices (CODE:amount)</Label><Input value={toStr(p.prices)} onChange={(e) => updPkg(i, { prices: toMap(e.target.value) })} className="mt-1 font-mono text-xs" data-testid={`pkg-prices-${p.id}`} /></div>
                <div className="sm:col-span-2"><Label className="text-xs">Features (one per line)</Label><Textarea rows={3} value={(p.features || []).join("\n")} onChange={(e) => updPkg(i, { features: e.target.value.split("\n").filter(Boolean) })} className="mt-1" /></div>
              </div>
            </div>
          ))}
        </TabsContent>

        <TabsContent value="addons" className="mt-4 space-y-3">
          {(c.addons || []).map((a, i) => (
            <div key={a.id} className="flex flex-wrap items-end gap-3 bs-card p-4" data-testid={`addon-edit-${a.id}`}>
              <div className="flex-1 min-w-[180px]"><Label className="text-xs">Name</Label><Input value={a.name} onChange={(e) => updAddon(i, { name: e.target.value })} className="mt-1" /></div>
              <div className="flex-1 min-w-[220px]"><Label className="text-xs">Prices (CODE:amount)</Label><Input value={toStr(a.prices)} onChange={(e) => updAddon(i, { prices: toMap(e.target.value) })} className="mt-1 font-mono text-xs" /></div>
              <Button variant="ghost" size="icon" onClick={() => setC({ addons: c.addons.filter((_, j) => j !== i) })}><Trash2 className="h-4 w-4 text-destructive" /></Button>
            </div>
          ))}
          <Button variant="outline" className="rounded-full" onClick={() => setC({ addons: [...(c.addons || []), { id: `addon-${Date.now()}`, name: "New add-on", prices: {} }] })} data-testid="addon-add"><Plus className="mr-2 h-4 w-4" /> Add add-on</Button>
        </TabsContent>

        <TabsContent value="currency" className="mt-4 grid max-w-lg gap-4 bs-card p-6">
          <div><Label className="text-xs">Default currency</Label><Input value={c.default_currency || ""} onChange={(e) => setC({ default_currency: e.target.value.toUpperCase() })} className="mt-1" data-testid="default-currency" /></div>
          <div><Label className="text-xs">Currencies (CODE:symbol, one per line)</Label>
            <Textarea rows={4} value={(c.currencies || []).map((x) => `${x.code}:${x.symbol}`).join("\n")}
              onChange={(e) => setC({ currencies: e.target.value.split("\n").filter(Boolean).map((l) => { const [code, ...rest] = l.split(":"); return { code: (code || "").trim().toUpperCase(), symbol: rest.join(":").trim() }; }) })}
              className="mt-1 font-mono text-xs" /></div>
          <div className="flex items-center justify-between"><Label className="text-xs">Tax enabled</Label><Switch checked={!!c.tax?.enabled} onCheckedChange={(v) => setC({ tax: { ...(c.tax || {}), enabled: v } })} data-testid="tax-enabled" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs">Tax label</Label><Input value={c.tax?.label || ""} onChange={(e) => setC({ tax: { ...(c.tax || {}), label: e.target.value } })} className="mt-1" /></div>
            <div><Label className="text-xs">Tax percent</Label><Input type="number" value={c.tax?.percent ?? 0} onChange={(e) => setC({ tax: { ...(c.tax || {}), percent: Number(e.target.value) } })} className="mt-1" /></div>
          </div>
          <div><Label className="text-xs">Proposal expiry (days)</Label><Input type="number" value={c.proposal?.expiry_days ?? 14} onChange={(e) => setC({ proposal: { ...(c.proposal || {}), expiry_days: Number(e.target.value) } })} className="mt-1" /></div>
          <div><Label className="text-xs">Proposal terms</Label><Textarea rows={4} value={c.proposal?.terms || ""} onChange={(e) => setC({ proposal: { ...(c.proposal || {}), terms: e.target.value } })} className="mt-1" /></div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
